
import React from 'react';
import { Controller, Control } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Mail, Phone } from 'lucide-react';
import { RegistrationFormValues } from '@/schemas/registrationSchema';

interface ContactInfoFieldsProps {
  control: Control<RegistrationFormValues>;
}

// Function to format phone number as xxxx-xxx-xxx
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  
  // Apply the formatting based on the number of digits
  if (digits.length <= 4) {
    return digits;
  } else if (digits.length <= 7) {
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  } else {
    return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7, 10)}`;
  }
};

// Function to normalize the phone input (remove formatting for form value)
const normalizePhoneNumber = (value: string): string => {
  return value.replace(/-/g, '');
};

// Function to validate email format
const validateEmail = (email: string): boolean => {
  const emailRegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return email ? emailRegExp.test(email) : false;
};

// Function to validate phone format (Vietnamese phone numbers)
const validatePhone = (phone: string): boolean => {
  // More permissive phone validation
  // Just check if there are at least 9 digits
  const normalizedPhone = normalizePhoneNumber(phone || '');
  return normalizedPhone.length >= 9 && normalizedPhone.length <= 11;
};

const ContactInfoFields: React.FC<ContactInfoFieldsProps> = ({ control }) => {
  console.log("Rendering ContactInfoFields");
  
  return (
    <div className="space-y-4">
      <FormItem>
        <FormLabel className="flex items-center gap-2">
          <Mail size={16} className="text-blue-500" />
          Email <span className="text-red-500 font-bold">*</span>
        </FormLabel>
        <FormControl>
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <>
                <Input
                  {...field}
                  type="email"
                  placeholder="Nhập email của bạn"
                  className={`transition-colors duration-200 ${
                    fieldState.error 
                      ? "border-red-500 animate-shake" 
                      : field.value 
                        ? validateEmail(field.value) 
                          ? "border-green-400" 
                          : "border-amber-300" 
                        : ""
                  }`}
                  aria-invalid={fieldState.error ? "true" : "false"}
                  aria-required="true"
                  required
                  onChange={(e) => {
                    field.onChange(e);
                    // Visual indication while typing
                    const isValid = validateEmail(e.target.value);
                    e.target.classList.toggle('border-amber-300', e.target.value && !isValid);
                    e.target.classList.toggle('border-green-400', e.target.value && isValid);
                  }}
                />
                {!fieldState.error && field.value && !validateEmail(field.value) && (
                  <div className="text-amber-500 text-xs md:text-sm mt-1 animate-in fade-in-50 duration-200">
                    Email không đúng định dạng (ví dụ: example@domain.com)
                  </div>
                )}
                {!field.value && (
                  <div className="text-amber-500 text-xs md:text-sm mt-1 animate-in fade-in-50 duration-200">
                    Email là trường bắt buộc
                  </div>
                )}
              </>
            )}
          />
        </FormControl>
        <FormMessage className="text-red-500 animate-in fade-in-50 duration-200 text-xs md:text-sm" />
      </FormItem>

      <FormItem>
        <FormLabel className="flex items-center gap-2">
          <Phone size={16} className="text-blue-500" />
          Số điện thoại <span className="text-red-500 font-bold">*</span>
        </FormLabel>
        <FormControl>
          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState }) => {
              console.log("Phone field value:", field.value);
              console.log("Phone field error:", fieldState.error);
              return (
                <>
                  <Input
                    value={formatPhoneNumber(field.value || '')}
                    onChange={(e) => {
                      const formattedValue = formatPhoneNumber(e.target.value);
                      const normalizedValue = normalizePhoneNumber(formattedValue);
                      
                      // Update the input with formatted value but pass normalized value to form
                      e.target.value = formattedValue;
                      field.onChange(normalizedValue);
                      
                      // Add visual validation feedback while typing
                      const isValid = validatePhone(normalizedValue);
                      e.target.classList.toggle('border-amber-300', normalizedValue && !isValid);
                      e.target.classList.toggle('border-green-400', normalizedValue && isValid);
                    }}
                    onBlur={(e) => {
                      // Format on blur to ensure display is consistent
                      e.target.value = formatPhoneNumber(field.value || '');
                      field.onBlur();
                    }}
                    type="tel"
                    placeholder="Nhập số điện thoại của bạn"
                    className={`transition-colors duration-200 ${
                      fieldState.error 
                        ? "border-red-500 animate-shake" 
                        : field.value 
                          ? validatePhone(field.value) 
                            ? "border-green-400" 
                            : "border-amber-300" 
                          : ""
                    }`}
                    aria-invalid={fieldState.error ? "true" : "false"}
                    aria-required="true"
                    required
                    maxLength={12} // Account for hyphens: xxxx-xxx-xxx
                  />
                  {!fieldState.error && field.value && !validatePhone(field.value) && (
                    <div className="text-amber-500 text-xs md:text-sm mt-1 animate-in fade-in-50 duration-200">
                      Số điện thoại phải có từ 9-11 số
                    </div>
                  )}
                  {!field.value && (
                    <div className="text-amber-500 text-xs md:text-sm mt-1 animate-in fade-in-50 duration-200">
                      Số điện thoại là trường bắt buộc
                    </div>
                  )}
                </>
              );
            }}
          />
        </FormControl>
        <FormMessage className="text-red-500 animate-in fade-in-50 duration-200 text-xs md:text-sm" />
      </FormItem>
    </div>
  );
};

export default ContactInfoFields;
